// Order Lookup and the order detail popup (lane D2, ask A33; backoffice parity pages/orders.md §1–§2, SCR-033,
// EP-ORD-17): the location's orders filtered as legacy's Order Lookup filters them, and one order as the detail popup
// shows it (items with their modifiers, the order summary, every payment with what was refunded or voided on it).
import { z } from 'zod';
import { defineQuery } from '../../define/operations.ts';
import { secret } from '../../define/secret.ts';
import { CheckId, DeviceId, EmployeeId, IsoInstant, LocalDate, LocationId, Money, OrderId, OrderLineId, Qty, Rate, RefundId, TableId, TenderId } from '../../primitives/codecs.ts';
import { OrderCustomer, OrderNotFound } from './index.ts';

const OrderStatus = z.enum(['open', 'sent', 'closed', 'voided', 'abandoned']);
/** The payment-type icon of a row and a payment: the first payment's method on the list. */
const PaymentMethod = z.enum(['cash', 'card', 'other', 'gift_card']);

export const OrderLookupRow = z
  .object({
    orderId: OrderId,
    number: z.string(),
    status: OrderStatus,
    openedAt: IsoInstant,
    businessDate: LocalDate,
    employeeId: EmployeeId.nullable(),
    paymentMethod: PaymentMethod.nullable(),
    subtotal: Money,
    discounts: Money,
    gratuity: Money,
    tax: Money,
    total: Money,
    /** True Margin: the cash discount the order's payments redeemed (the column shows on True Margin only). */
    cashDiscount: Money,
    /**
     * The register's Order Lookup columns (lane D6, field note register.order-lookup): Cust/Order Name (the tab's name,
     * else the customer's), the Table, and the Ticket of each payment (its drawer's letter and the order number, as
     * Reconcile Tips shows it), in payment order. Defaulted, so an answer from a server before them still parses.
     */
    name: z.string().nullable().default(null),
    tableId: TableId.nullable().default(null),
    tickets: z.array(z.string()).default([]),
    /**
     * Where the order was taken (OrderOpened v3): `online`, an online order recorded on the location, which legacy lists
     * with "Online Order" for its employee and the online icon and status. Defaulted, so an older server's answer parses.
     */
    channel: z.enum(['pos', 'online']).default('pos'),
  })
  .meta({ id: 'OrderLookupRow' });

export const LookupOrders = defineQuery({
  name: 'orders.lookup',
  summary:
    "Order Lookup: the location's closed and voided orders opened on the business dates from…to, newest first, filtered by status, order number, employee, total and the last 4 of a card, a page at a time with the record count when a page is asked for; an order id finds that one order and ignores every other filter.",
  input: z.object({
    locationId: LocationId,
    from: LocalDate,
    to: LocalDate,
    orderId: OrderId.optional(),
    number: z.string().min(1).max(32).optional(),
    employeeId: EmployeeId.optional(),
    /** The register's Device filter (legacy `device_id`): orders rung on that device's drawers. */
    deviceId: DeviceId.optional(),
    minTotal: Money.optional(),
    maxTotal: Money.optional(),
    /** Legacy "Last 4 of card": orders with a payment by a card ending in these digits (the cardholder's: secret). */
    cardLast4: secret(z.string().regex(/^\d{4}$/)).optional(),
    /**
     * B1867 (legacy api 733a2f646 `status`: all, complete, void): the Back Office lists completed and voided orders, All
     * when absent; `closed` or `voided` narrows it. Orders still open, sent, abandoned or (imported) paid never appear,
     * but one the business-day close abandoned is voided (legacy's auto-close voids it), row status `voided`. An order id
     * finds only a closed or voided order whatever this says (core `orderLookupStatuses`, `orderLookupStatusOf`). Absent
     * before 2026-11-01; the register's lookup has no status and lists every order.
     */
    status: z.enum(['all', 'closed', 'voided']).optional(),
    /**
     * Legacy's server paging (OrderLookupController `paginate(perPage: rowsPerPage ?? 30, page: page ?? 1)`): with
     * `page`, the answer is that page of `perPage` rows (30 when absent) of the whole newest-first list, with its total.
     * Without it, the newest 500, as a register asks.
     */
    page: z.int().min(1).optional(),
    perPage: z.int().min(1).max(10_000).optional(),
  }),
  output: z.object({
    orders: z.array(OrderLookupRow),
    /** How many orders the filters match, when a page was asked for (legacy `pagination.total_records`). */
    recordCount: z.int().nonnegative().optional(),
  }),
  permission: 'order_lookup.view',
  availability: 'location',
  scope: 'location',
  errors: [],
  agent: { expose: true, surfaces: ['backoffice.assistant'] },
  legacy: ['POST /v1/orders/lookup'],
});

const DetailModifier = z.object({ name: z.string(), price: Money });

export const OrderDetailItem = z
  .object({
    lineId: OrderLineId,
    name: z.string(),
    variantName: z.string().nullable(),
    quantity: Qty,
    /** (unit + modifiers) × quantity. */
    price: Money,
    modifiers: z.array(DetailModifier),
    /** Every unit was voided after it was sent: listed as Deleted. */
    deleted: z.boolean(),
    /** The check the line is on, on an order split by item (an even split shares every line). Absent before 2026-10-14. */
    checkId: CheckId.optional(),
  })
  .meta({ id: 'OrderDetailItem' });

export const OrderDetailPayment = z
  .object({
    tenderId: TenderId,
    method: PaymentMethod,
    /** A check or a custom tender type's name; null for cash, card and gift card. */
    label: z.string().nullable(),
    /**
     * An "other" payment's kind (legacy PaymentIcon's tile by payment type): a check, a house account, a loyalty
     * redemption, or a custom tender type; null for cash, card and gift card. Absent before 2026-10-18.
     */
    otherKind: z.enum(['check', 'house_account', 'loyalty', 'tender_type']).nullable().optional(),
    cardBrand: z.string().nullable(),
    cardLast4: z.string().nullable(),
    /** Applied to the order, ex tip. */
    total: Money,
    tip: Money,
    paid: Money,
    /** Payment void: when the payment was voided (legacy "Time Voided"), else null. */
    voidedAt: IsoInstant.nullable(),
    /** What refunds gave back on this payment (amount and tip). */
    refunded: Money,
    /**
     * Legacy's Ticket ID: the drawer's letter and the order number ("A-12"), or the ticket legacy printed. Absent before
     * 2026-10-13. Up to 255 characters, as the import keeps legacy's `tenders.ticket` (a string(255)); it was 64, and one
     * long legacy ticket failed its whole order's detail (B686). Widening an output is backward-compatible.
     */
    ticket: z.string().max(255).nullable().optional(),
    /** The check it paid on, on a split order. Absent when the order is not split, and before 2026-10-14. */
    checkId: CheckId.optional(),
    /**
     * B1608 (legacy main b4bfde902 DrawerOrderPopupPayment): a custom tender type's tracking detail (legacy
     * `transaction_id` on an other tender), which its name line prints after the type's name ("DoorDash #A1B2C3"). Absent
     * without one, and before 2026-10-28.
     */
    details: z.string().max(255).optional(),
    /**
     * B1886: a check payment's number as the cashier keyed it (legacy `transaction_id` on a check), which a stored
     * reprint names it by ("Check #1234"). Absent without one, on an imported check, and before 2026-11-01; an older
     * reader drops it and prints "Check".
     */
    checkNumber: z.string().min(1).max(255).optional(),
    /** B1608: legacy `tenders.fee`, the NCA Fee cell on a Non-cash Adjustment location (a card's fee). Absent before 2026-10-28. */
    fee: Money.optional(),
    /** B1608: the signed cash change rounding on a cash payment (legacy `cash_rounding_amount`). Absent when nothing was rounded. */
    cashRounding: Money.optional(),
    /**
     * B1986: what the customer handed over on a cash payment (a live payment's `TenderCaptured.tendered`, an imported one's
     * `TenderImported.tendered`, legacy `tenders.tendered_amount`), which a reprinted cash receipt prints with the change
     * (core `cashChangeGiven`), as legacy's reprint prints `amount_tendered` and `change_given`. Absent when none was stored
     * (legacy's tender columns off, a payment taken before) and before 2026-11-03; an older reader prints the applied amount.
     */
    tendered: Money.optional(),
  })
  .meta({ id: 'OrderDetailPayment' });

export const OrderDetailRefund = z
  .object({
    refundId: RefundId,
    requestedAt: IsoInstant,
    status: z.enum(['requested', 'completed', 'failed']),
    amount: Money,
    tip: Money,
    lines: z.array(z.object({ lineId: OrderLineId, name: z.string(), quantity: Qty, amount: Money })),
    /**
     * B1608: what the refund gave back on each payment (legacy `tender_refunds`, one Payments line each, named from the
     * refunded payment). Absent before 2026-10-28.
     */
    tenders: z.array(z.object({ tenderId: TenderId, amount: Money, tip: Money })).optional(),
  })
  .meta({ id: 'OrderDetailRefund' });

export const OrderDetail = z
  .object({
    orderId: OrderId,
    number: z.string(),
    status: OrderStatus,
    openedAt: IsoInstant,
    closedAt: IsoInstant.nullable(),
    voidedAt: IsoInstant.nullable(),
    employeeId: EmployeeId.nullable(),
    tabName: z.string().nullable(),
    /**
     * "Hide name on customer receipt" (legacy `tabs.receipt_print_order`; A54): when true, a customer receipt reprinted
     * from this order prints no name at all. Absent reads as false (a server before 2026-10-13, or an order that never set it).
     */
    hideNameOnReceipt: z.boolean().optional(),
    /**
     * The order's customer, with the phone and address its receipts print (a live order's attach snapshot, an imported
     * one's tab; legacy `tab_phone`, `tab_address_1/2`): a reprint prints them. Absent without one, and before 2026-10-16.
     */
    customer: OrderCustomer.optional(),
    tableId: TableId.nullable(),
    /**
     * The name of the floor-plan table it shows (legacy `order.table_name`, OrderResource `tab.floorPlanItem.name`), which
     * the back office's detail draws as a chip with the time the order has been open (B1596). Absent when the table has no
     * name (a numbered table may not), without a table, and before 2026-10-31.
     */
    tableName: z.string().max(255).optional(),
    /** The dining option's name (legacy's band above the Order Summary); null without one. Absent before 2026-10-13. */
    diningOption: z.string().max(255).nullable().optional(),
    items: z.array(OrderDetailItem),
    /**
     * A split order's checks (imported orders: legacy `order_splits`; B1962, a live order's checks since 2026-11-02), each
     * with what its payments charged: legacy stored no check totals, so a check's total is the sum of its payments that
     * still count, tips not counted and a card's Non-Cash Adjustment fee included (live: the payments' applied amounts;
     * imported: legacy `tenders.amount`, which carries the card's fee). Absent when the order is not split, and before
     * 2026-10-14.
     */
    checks: z
      .object({
        mode: z.enum(['even', 'by_item']),
        checks: z.array(z.object({
          checkId: CheckId, checkNumber: z.int().positive(), total: Money,
          /**
           * B1962: the totals block a reprinted card receipt of a payment on this check prints, from stored figures
           * (legacy StarPrintCard). On an even split it is the whole order's block on every check (legacy prints the
           * whole order), with `fee` the checks' stored fees summed. On a split by item it is the check's own stored
           * money (OrderPriced's check; `tax` its cash total less the rest, as legacy's block sums), with `fee` the
           * check's one card's stored surcharge only when that card covered the check's cash total; it is absent when the
           * order has an amount cart discount, which legacy takes whole on every ticket and nothing stores, and on the
           * last check, which the stored reconcile gives the order's leftover cents where legacy's receipt prices it alone. `cashTotal` is
           * the total at the cash price. Absent on an imported order's check (legacy stored none) and before 2026-11-02.
           */
          money: z.object({ subtotal: Money, discount: Money, gratuity: Money, tax: Money, cashTotal: Money, fee: Money.optional() }).optional(),
          /**
           * B1976 N6: the Order Total a reprinted cash, gift card or other-tender receipt of a payment on this check
           * prints, from stored figures (legacy StarPrintCash / StarPrintGiftCard `getCheckTotals`): on an even split the
           * order's stored cash total on every check, as legacy prices every item; on a split by item (B1986) the check's
           * own stored cash total, as legacy prices the ticket's items. Absent with an amount cart discount (legacy takes
           * the ticket's share, which nothing stores), on an imported order's check, and before 2026-11-02; the check's
           * paid `total` prints then.
           */
          otherTenderTotal: Money.optional(),
          /**
           * B1986 at True Margin: the totals block a reprinted tender receipt of a payment on this check prints at a True
           * Margin location (legacy StarPrintCashTrueMargin, StarPrintOtherTrueMargin and StarPrintCardTrueMargin, all from
           * `getTrueMarginTotalsFromTender`): Subtotal, Discounts, Gratuity, Cash Discount (`cashDiscountRedeemed`), Sales
           * Tax and Order Total (the card column with the check's tenders counted), then the dues still owed and what the
           * check's tenders paid. Sent for an imported order split by item (core `importedByItemTrueMarginCheckReprints`);
           * absent elsewhere, where legacy's figure cannot be had, and before 2026-11-03; the check's paid `total` prints then.
           */
          trueMargin: z.object({
            subtotal: Money, discount: Money, gratuity: Money, cashDiscountRedeemed: Money, tax: Money, total: Money, cardDue: Money, cashDue: Money, paid: Money,
          }).optional(),
        })).min(2).max(64),
      })
      .optional(),
    summary: z.object({
      subtotal: Money, discount: Money, gratuity: Money, cashDiscount: Money, tax: Money, total: Money, tip: Money, paid: Money,
      /**
       * B1962: the order's gratuity percent as stored (a live order's active percent service charge, an imported order's
       * legacy `orders.gratuity`), which a reprinted card receipt prints as "Gratuity (18%)"; zero with no percent
       * gratuity, as the register's own receipt reads it. Absent before 2026-11-02 (an older node).
       */
      gratuityRate: Rate.optional(),
      /**
       * B1988: the Order Total a reprinted receipt prints: an online order's items priced again as legacy's reprint
       * prices them (without the tip, which legacy's stored `orders.total` holds but its reprint prints only on its own
       * Tip line), else the stored `total`. Absent before 2026-11-03 (an older node); `total` prints then.
       */
      receiptTotal: Money.optional(),
      /**
       * B1988 (money review of #3079, B1): the Sales Tax a reprinted receipt prints, where it differs from how `tax` is
       * read: an online order's items priced again as legacy's tender reprint prices them (each line's tax rounded on its
       * own, core `onlineReprintTotals`), where `tax` is the online tax rounded once on the subtotal; `receiptTotal` is
       * then the subtotal plus it. Absent for a register order, and before 2026-11-03 (an older node); `tax` prints then.
       */
      receiptTax: Money.optional(),
      /**
       * B1962: the Non-Cash Adjustment the order's payments that still count were charged (a live order's card
       * surcharges; an imported order's stored legacy surcharge), which a reprinted card receipt prints. Absent before
       * 2026-11-02.
       */
      fee: Money.optional(),
      /**
       * B1962: whether the order is split into checks (a live order's `checks`, an imported order's `order_splits`). A
       * reprinted card receipt prints the order's totals block only when this says false: a check's own figures are not
       * in the summary. Absent before 2026-11-02 (an older node), which prints as before.
       */
      split: z.boolean().optional(),
    }),
    payments: z.array(OrderDetailPayment),
    refunds: z.array(OrderDetailRefund),
    /**
     * Legacy's `drawer_open` (api OrderResource): false once the order's drawer has closed, and for an online order (legacy
     * sends it none); true with no drawer. The register's Order Lookup offers Void Order and Void Payment only while it is
     * true (WF-51/52). Absent before 2026-10-18: unknown, so the register offers the void and the node decides.
     */
    drawerOpen: z.boolean().optional(),
  })
  .meta({ id: 'OrderDetail' });

export const GetOrderDetail = defineQuery({
  name: 'orders.getOrderDetail',
  summary: 'One order as the backoffice detail popup shows it: items, the order summary, each payment with what was refunded or voided on it, and the refunds.',
  input: z.object({ orderId: OrderId }),
  output: OrderDetail,
  permission: 'order_lookup.view',
  availability: 'location',
  scope: 'location',
  errors: [OrderNotFound],
  agent: { expose: true, surfaces: ['backoffice.assistant'] },
  legacy: ['GET /v1/orders/{id}'],
});

/**
 * The register's Order Lookup (lane D6, field note register.order-lookup): the same lookup and detail on the register's
 * own session. Legacy's register asks no permission for Order Lookup (its admin menu item only routes), and a register's
 * device session carries no staff permission, so these take the device session as the customer and catalog register
 * queries do; the back office keeps `order_lookup.view`.
 */
export const LookupOrdersAtRegister = defineQuery({
  name: 'orders.lookupAtRegister',
  summary: "The register's Order Lookup: orders.lookup on the register's device session, listing orders of every status.",
  // B1867: legacy's register lookup (`OrderLookupSearch`, POST /v1/orders/order-lookup) has no status rule, so it lists
  // open orders too; the Back Office's status filter is not part of it.
  input: LookupOrders.input.omit({ status: true }),
  output: LookupOrders.output,
  permission: { session: 'device' },
  availability: 'location',
  scope: 'location',
  errors: [],
  agent: { expose: false },
  legacy: ['POST /v1/orders/order-lookup'],
});

export const GetOrderDetailAtRegister = defineQuery({
  name: 'orders.orderDetailAtRegister',
  summary: "One order as the register's Order Lookup shows it when the register does not hold it: orders.getOrderDetail on the device session.",
  input: GetOrderDetail.input,
  output: GetOrderDetail.output,
  permission: { session: 'device' },
  availability: 'location',
  scope: 'location',
  errors: [OrderNotFound],
  agent: { expose: false },
  legacy: ['GET /v1/orders/{id}'],
});

export const orderLookupQueries = [LookupOrders, GetOrderDetail, LookupOrdersAtRegister, GetOrderDetailAtRegister] as const;
